import { useState, useEffect } from 'react';
import { fetchCarriers, savePolicy, enrichHome } from '../../utils/insuranceApi';
import { loadClients } from '../../utils/clientDataLoader';
import { annualizePremium } from '../../utils/annualizePremium';

const PRODUCT_OPTIONS = [
  'auto', 'home', 'umbrella', 'life', 'disability', 'watercraft', 'wind', 'flood',
];

const STATUS_OPTIONS = ['active', 'prospect', 'expired', 'cancelled', 'lapsed'];

// Inline label/input style helpers (matches our brand)
const labelClass = 'block text-[10px] font-bold uppercase tracking-[0.08em] text-navy mb-1';
const inputClass = 'w-full px-2 py-1.5 text-[12px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue';

export default function ExtractionReviewForm({
  extractedData,
  documentId,
  fileName,
  queuePosition, // { current, total } when reviewing a batch
  existingPolicy, // when editing, pass the saved policy record
  onSave,
  onCancel,
}) {
  const [carriers, setCarriers] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isEditing = !!existingPolicy?.id;
  const e = extractedData || {};

  // Form state - pre-populated from existing policy (edit) or extraction (new)
  const [productType, setProductType] = useState(existingPolicy?.product_type || e.product_type || 'auto');
  const [carrierId, setCarrierId] = useState(existingPolicy?.carrier_id || '');
  const [carrierName] = useState(e.carrier_name || existingPolicy?.extracted_data?.carrier_name || '');
  const [policyNumber, setPolicyNumber] = useState(existingPolicy?.policy_number || e.policy_number || '');
  const [effectiveDate, setEffectiveDate] = useState(existingPolicy?.effective_date || e.effective_date || '');
  const [expirationDate, setExpirationDate] = useState(existingPolicy?.expiration_date || e.expiration_date || '');
  // For new uploads, prefer life/disability premium if available (those usually carry the
  // payment-mode), otherwise fall back to top-level premium_total. Always store the annualized
  // figure, since premium_annual is the on-disk column.
  const initialRawPremium = e.life_specific?.premium_amount ?? e.disability_specific?.premium_amount ?? e.premium_total ?? null;
  const initialMode = e.life_specific?.premium_mode || e.disability_specific?.premium_mode || e.premium_term;
  const initialAnnualized = existingPolicy?.premium_annual ?? annualizePremium(initialRawPremium, initialMode);
  const [premium, setPremium] = useState(initialAnnualized || '');
  // Stored premium is always the annualized figure, so the term defaults to Annual
  // unless the user is editing an existing policy that already had a different term.
  const [premiumTerm, setPremiumTerm] = useState(existingPolicy?.premium_term || 'Annual');
  const [householdId, setHouseholdId] = useState(existingPolicy?.household_id || '');
  const [status, setStatus] = useState(existingPolicy?.status || 'active');
  const [notes, setNotes] = useState(existingPolicy?.notes || e.extraction_notes || '');
  const [weBroker, setWeBroker] = useState(existingPolicy?.we_broker || false);
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);

  // Named insured (for matching to households)
  const ni = e.named_insured || {};
  const niName = `${ni.first_name || ''} ${ni.last_name || ''}`.trim();

  useEffect(() => {
    fetchCarriers().then(list => {
      setCarriers(list);
      // Try to auto-match carrier name to a carrier_id
      if (e.carrier_name) {
        const match = list.find(c => c.name.toLowerCase() === e.carrier_name.toLowerCase());
        if (match) setCarrierId(match.id);
      }
    }).catch(() => {});

    // Build household list from clients data, try to auto-match
    const clients = loadClients();
    const seenHouseholds = new Map();
    for (const c of clients) {
      if (c.householdId && !seenHouseholds.has(c.householdId)) {
        seenHouseholds.set(c.householdId, c.householdName || c.name);
      }
    }
    const list = Array.from(seenHouseholds.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    setHouseholds(list);

    // Try to match named insured to a household
    if (niName) {
      const lowerNi = niName.toLowerCase();
      const lastName = (ni.last_name || '').toLowerCase();
      const match = list.find(h =>
        h.name.toLowerCase().includes(lowerNi) ||
        (lastName && h.name.toLowerCase().includes(lastName))
      );
      if (match) setHouseholdId(match.id);
    }
  }, [e.carrier_name, niName, ni.last_name]);

  async function handleSave() {
    if (!householdId) {
      setError('Please select a household for this policy');
      return;
    }
    setSaving(true);
    setError(null);

    try {
      // Find or use carrier_id
      let finalCarrierId = carrierId || null;
      if (!finalCarrierId && carrierName) {
        // No carrier match - we'll just save the name in extracted_data
        // (could add a "create new carrier" flow later)
      }

      const payload = {
        household_id: householdId,
        product_type: productType,
        carrier_id: finalCarrierId,
        policy_number: policyNumber || null,
        effective_date: effectiveDate || null,
        expiration_date: expirationDate || null,
        premium_annual: premium ? parseFloat(premium) : null,
        premium_term: premiumTerm,
        status,
        we_broker: weBroker,
        notes,
      };
      // Only set source_document_id and extracted_data on initial creation
      if (!isEditing) {
        payload.source_document_id = documentId;
        payload.extracted_data = extractedData;
      }
      // For edits, include the id so savePolicy uses PUT
      if (isEditing) payload.id = existingPolicy.id;

      const policy = await savePolicy(payload);
      onSave?.(policy);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Render either auto- or home-specific summary
  function renderProductSpecific() {
    if (productType === 'auto' && e.auto_specific) {
      const a = e.auto_specific;
      return (
        <div className="bg-bone/30 rounded-[3px] p-3 text-[11px] text-[#666]">
          <div className="font-bold text-navy mb-1">Vehicles ({a.vehicles?.length || 0})</div>
          {(a.vehicles || []).map((v, i) => (
            <div key={i}>{v.year} {v.make} {v.model} {v.trim || ''} {v.vin ? `· ${v.vin}` : ''}</div>
          ))}
          <div className="font-bold text-navy mt-2 mb-1">Drivers ({a.drivers?.length || 0})</div>
          {(a.drivers || []).map((d, i) => (
            <div key={i}>{d.first_name} {d.last_name} {d.dob ? `(DOB: ${d.dob})` : ''}</div>
          ))}
        </div>
      );
    }
    if (productType === 'home' && e.home_specific) {
      const h = e.home_specific;
      return (
        <div className="bg-bone/30 rounded-[3px] p-3 text-[11px] text-[#666]">
          <div className="grid grid-cols-3 gap-2">
            <div><b>Built:</b> {h.year_built || '?'}</div>
            <div><b>Sqft:</b> {h.square_feet || '?'}</div>
            <div><b>Form:</b> {h.policy_form || '?'}</div>
            <div><b>Construction:</b> {h.construction_type || '?'}</div>
            <div><b>Roof:</b> {h.roof_type || '?'}</div>
            <div><b>Roof Age:</b> {h.roof_age_years ? `${h.roof_age_years} yrs` : '?'}</div>
            <div><b>Dwelling:</b> ${(h.dwelling_coverage || 0).toLocaleString()}</div>
            <div><b>Personal:</b> ${(h.personal_property_coverage || 0).toLocaleString()}</div>
            <div><b>Liability:</b> ${(h.liability_coverage || 0).toLocaleString()}</div>
            <div><b>Deductible:</b> ${(h.deductible || 0).toLocaleString()}</div>
            <div><b>Wind Ded:</b> ${(h.wind_deductible || 0).toLocaleString()}</div>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-[4px] shadow-[0_8px_30px_rgba(0,0,0,0.15)] w-[760px] max-w-full max-h-[90vh] overflow-y-auto p-5">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-[14px] font-bold text-navy">
              {isEditing ? 'Edit Policy' : 'Review Extracted Policy'}
            </h3>
            <p className="text-[11px] text-[#888] mt-0.5">
              {isEditing
                ? 'Update any fields and save your changes.'
                : 'AI extracted these fields from your PDF. Verify, correct anything wrong, then save.'}
            </p>
            {fileName && !isEditing && (
              <p className="text-[10px] text-[#aaa] mt-0.5">From: {fileName}</p>
            )}
          </div>
          {queuePosition && (
            <div className="bg-bullogic-blue/10 text-bullogic-blue px-2.5 py-1 rounded-[3px] text-[11px] font-bold whitespace-nowrap">
              {queuePosition.current} of {queuePosition.total}
            </div>
          )}
        </div>

        {/* Named insured summary (read-only context) */}
        {niName && (
          <div className="bg-bullogic-blue/5 border border-bullogic-blue/20 rounded-[3px] px-3 py-2 mb-3 text-[12px]">
            <span className="font-bold text-navy">Named Insured: </span>
            <span>{niName}</span>
            {ni.dob && <span className="text-[#888]"> · DOB {ni.dob}</span>}
            {e.address?.city && <span className="text-[#888]"> · {e.address.city}, {e.address.state}</span>}
          </div>
        )}

        {/* Form fields */}
        <div className="space-y-3">
          {/* Row 1: Household + Product */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Household *</label>
              <select value={householdId} onChange={e => setHouseholdId(e.target.value)} className={inputClass}>
                <option value="">-- Select household --</option>
                {households.map(h => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Product Type</label>
              <select value={productType} onChange={e => setProductType(e.target.value)} className={inputClass}>
                {PRODUCT_OPTIONS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Carrier + Policy # */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Carrier</label>
              <select value={carrierId} onChange={e => setCarrierId(e.target.value)} className={inputClass}>
                <option value="">-- {carrierName || 'Select carrier'} --</option>
                {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Policy Number</label>
              <input type="text" value={policyNumber} onChange={e => setPolicyNumber(e.target.value)} className={inputClass} />
            </div>
          </div>

          {/* Row 3: Dates */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Effective Date</label>
              <input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Expiration Date</label>
              <input type="date" value={expirationDate} onChange={e => setExpirationDate(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Term</label>
              <select value={premiumTerm} onChange={e => setPremiumTerm(e.target.value)} className={inputClass}>
                <option value="6 Month">6 Month</option>
                <option value="12 Month">12 Month</option>
                <option value="Annual">Annual</option>
              </select>
            </div>
          </div>

          {/* Row 4: Premium + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Premium ($)</label>
              <input type="number" value={premium} onChange={e => setPremium(e.target.value)} className={inputClass} placeholder="0" />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className={inputClass}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Broker flag */}
          <div className="flex items-center gap-2 bg-bullogic-blue/5 border border-bullogic-blue/20 rounded-[3px] px-3 py-2">
            <input
              type="checkbox"
              id="we_broker"
              checked={weBroker}
              onChange={e => setWeBroker(e.target.checked)}
              className="h-4 w-4 cursor-pointer"
            />
            <label htmlFor="we_broker" className="text-[12px] text-navy cursor-pointer select-none">
              <span className="font-bold">We brokered this policy</span>
              <span className="text-[#888]"> -- check if we sold/placed this policy (vs uploaded for tracking only)</span>
            </label>
          </div>

          {/* Home enrichment button (only for home + when we have a policy id and address) */}
          {productType === 'home' && existingPolicy?.id && e.address?.street && (
            <div className="bg-deep-teal/5 border border-deep-teal/20 rounded-[3px] px-3 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[12px]">
                  <div className="font-bold text-navy">Home Data Enrichment</div>
                  <div className="text-[11px] text-[#888]">
                    Look up year built, sqft, roof type, and other details from public listings
                  </div>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    setEnriching(true);
                    setEnrichResult(null);
                    try {
                      const result = await enrichHome(existingPolicy.id);
                      setEnrichResult({
                        success: true,
                        message: result.fromCache
                          ? `Used cached enrichment for this address`
                          : `Enriched ${Object.values(result.enrichment || {}).filter(v => v != null && v !== '').length} fields`,
                      });
                    } catch (err) {
                      setEnrichResult({ success: false, message: err.message });
                    } finally {
                      setEnriching(false);
                    }
                  }}
                  disabled={enriching}
                  className={`px-3 py-1.5 text-[11px] font-bold rounded-[3px] whitespace-nowrap ${
                    enriching ? 'bg-gray-200 text-gray-400 cursor-wait' : 'bg-deep-teal text-white hover:bg-deep-teal/90'
                  }`}
                >
                  {enriching ? 'Looking up...' : 'Enrich from Web'}
                </button>
              </div>
              {enrichResult && (
                <div className={`mt-2 text-[11px] ${enrichResult.success ? 'text-deep-teal' : 'text-rose'}`}>
                  {enrichResult.success ? '✓' : '✗'} {enrichResult.message}
                  {enrichResult.success && ' -- close and reopen this policy to see updated fields.'}
                </div>
              )}
            </div>
          )}

          {/* Product-specific summary (read-only, stored in extracted_data) */}
          {renderProductSpecific()}

          {/* Notes */}
          <div>
            <label className={labelClass}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={inputClass} />
          </div>
        </div>

        {error && (
          <div className="mt-3 p-2 bg-rose/10 text-rose text-[12px] rounded-[3px]">{error}</div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-100">
          <button onClick={onCancel} className="px-4 py-2 text-[12px] font-bold text-[#888] bg-gray-100 hover:bg-gray-200 rounded-[4px]">
            {queuePosition && queuePosition.total > 1 ? 'Skip' : 'Cancel'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !householdId}
            className={`px-4 py-2 text-[12px] font-bold rounded-[4px] ${
              saving || !householdId
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90'
            }`}
          >
            {saving ? 'Saving...' : 'Save as Policy'}
          </button>
        </div>
      </div>
    </div>
  );
}
